import { memo } from "react";
import { SWRConfig } from "swr";
import {
  ActionIcon,
  Group,
  Image,
  SegmentedControl,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import useDashboardFilters from "../../hooks/useDashboardFilters";
import useOperatorDashboardData from "../../hooks/useOperatorDashboardData";
import { DASHBOARD_OPERATOR_SWR_CONFIG } from "../../utils/dashboardQueryKeys";
import { applyDashboardRangeField } from "../../utils/dashboardDateRange";
import OperatorDashboardContent from "./OperatorDashboardContent";
import adminClasses from "../AdminDashboardView/AdminDashboardView.module.css";
import op from "./OperatorDashboardView.module.css";

const OperatorDashboardHeader = memo(
  ({
    operatorName,
    timeFrame,
    setTimeFrame,
    scope,
    setScope,
    filters,
    setFilters,
  }) => (
    <header className={op.header}>
      <div className={op.headerRow}>
        <div>
          <h1 className={op.title}>Operator Dashboard</h1>
          <p className={op.subtitle}>
            {operatorName || "Comprehensive overview of your insurance operations"}
          </p>
        </div>

        <Group gap={8} wrap="wrap" className={adminClasses.filterPill}>
          <SegmentedControl
            size="xs"
            className={op.segment}
            value={timeFrame}
            onChange={setTimeFrame}
            aria-label="Time period"
            data={[
              { label: "Daily", value: "D" },
              { label: "Weekly", value: "W" },
              { label: "Monthly", value: "M" },
            ]}
          />

          <div className={adminClasses.divider} />

          <Select
            size="xs"
            className={adminClasses.compactSelect}
            placeholder="Region"
            aria-label="Region scope"
            data={[
              { label: "Both", value: "both" },
              { label: "Domestic", value: "domestic" },
              { label: "International", value: "international" },
            ]}
            value={scope || "both"}
            onChange={(value) => setScope(value || "both")}
            allowDeselect={false}
          />

          <div className={adminClasses.divider} />

          <Group gap={6} wrap="nowrap" className={adminClasses.dateRange}>
            <DateInput
              value={filters.startDate}
              onChange={(value) =>
                setFilters((prev) => applyDashboardRangeField(prev, "startDate", value))
              }
              placeholder="From"
              size="xs"
              className={adminClasses.compactDate}
              maxDate={new Date()}
            />
            <Text className={adminClasses.arrow}>–</Text>
            <DateInput
              value={filters.endDate}
              onChange={(value) =>
                setFilters((prev) => applyDashboardRangeField(prev, "endDate", value))
              }
              placeholder="To"
              size="xs"
              className={adminClasses.compactDate}
              maxDate={new Date()}
              minDate={
                filters.startDate
                  ? dayjs(filters.startDate).startOf("day").toDate()
                  : undefined
              }
            />
            {(filters.startDate || filters.endDate) && (
              <Tooltip label="Reset dates" position="bottom" withArrow fz={11}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => setFilters({ startDate: null, endDate: null })}
                  aria-label="Reset date filter"
                >
                  <Image src="/images/svg/cross.svg" w={10} h={10} alt="" />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>
      </div>
    </header>
  )
);

OperatorDashboardHeader.displayName = "OperatorDashboardHeader";

const OperatorDashboardBody = () => {
  const { timeFrame, setTimeFrame, scope, setScope, filters, setFilters } =
    useDashboardFilters();
  const dashboard = useOperatorDashboardData({ timeFrame, scope, filters });

  return (
    <>
      <OperatorDashboardHeader
        operatorName={dashboard.operatorName}
        timeFrame={timeFrame}
        setTimeFrame={setTimeFrame}
        scope={scope}
        setScope={setScope}
        filters={filters}
        setFilters={setFilters}
      />
      <div className={op.body}>
        <OperatorDashboardContent {...dashboard} />
      </div>
    </>
  );
};

const OperatorDashboardView = () => (
  <div className={op.page}>
    <SWRConfig value={DASHBOARD_OPERATOR_SWR_CONFIG}>
      <OperatorDashboardBody />
    </SWRConfig>
  </div>
);

export default OperatorDashboardView;
